import { FaCheck, FaTimes } from "react-icons/fa";
import Card from "../../../components/Card";

export default function GoogleMeetCard({
  googleStatus,
  testResult,
  testLoading,
  handleTestGoogleMeet,
}) {
  if (!googleStatus) return null;
  return (
    <Card className="mb-6">
      <h2 className="text-xl font-semibold mb-4 text-text">Google Meet Integration</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-text mb-4">
        <div>
          <p className="text-sm opacity-70">Mode</p>
          <p className={`text-lg font-bold flex items-center gap-2 ${googleStatus.mode === "live" ? "text-secondary" : "text-accent"}`}>
            {googleStatus.mode === "live" ? <><FaCheck /> Live (Google API)</> : <><FaTimes /> Fallback (static links)</>}
          </p>
        </div>
        <div>
          <p className="text-sm opacity-70">Config Status</p>
          <p className="text-sm flex items-center gap-2">Client ID: {googleStatus.clientId ? <FaCheck className="text-secondary" /> : <FaTimes className="text-accent" />}</p>
          <p className="text-sm flex items-center gap-2">Client Secret: {googleStatus.clientSecret ? <FaCheck className="text-secondary" /> : <FaTimes className="text-accent" />}</p>
          <p className="text-sm flex items-center gap-2">Refresh Token: {googleStatus.refreshToken ? <FaCheck className="text-secondary" /> : <FaTimes className="text-accent" />}</p>
        </div>
      </div>
      {googleStatus.mode !== "live" && (
        <div className="p-4 bg-accent/10 border border-accent/30 rounded-lg">
          <p className="text-sm font-medium text-accent mb-3">Setup Required: Enable Google Meet</p>
          <ol className="text-sm space-y-2 text-text opacity-90">
            <li>1. Get OAuth URL: <code className="bg-background px-2 py-1 rounded text-xs">GET /api/v1/admin/google/auth-url</code></li>
            <li>2. Open the URL and grant permissions</li>
            <li>3. Copy the authorization code from the redirect URL</li>
            <li>4. Exchange code: <code className="bg-background px-2 py-1 rounded text-xs">POST /api/v1/admin/google/exchange-code</code></li>
            <li>5. Add <code className="bg-background px-2 py-1 rounded text-xs">GOOGLE_REFRESH_TOKEN</code> to .env</li>
            <li>6. Restart backend server</li>
          </ol>
          <p className="text-xs text-text opacity-70 mt-3">Note: The refresh token must include Calendar API scopes (https://www.googleapis.com/auth/calendar)</p>
        </div>
      )}
      {googleStatus.mode === "live" && (
        <div className="p-3 bg-secondary/10 border border-secondary/30 rounded-lg">
          <p className="text-sm text-secondary flex items-center gap-2">
            <FaCheck className="flex-shrink-0" /> Google Meet is properly configured. Real meet links will be generated for sessions.
          </p>
        </div>
      )}
      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-text/80">Run a live check to create a test Google Meet link and verify integration.</p>
        <button
          onClick={handleTestGoogleMeet}
          disabled={testLoading}
          className={`px-4 py-2 rounded-md text-white ${testLoading ? "bg-secondary/70 cursor-not-allowed" : "bg-secondary hover:bg-secondary/90"}`}
        >
          {testLoading ? "Testing…" : "Test Google Meet"}
        </button>
      </div>
      {testResult && (
        <div className={`mt-4 p-4 border rounded-lg ${testResult.success ? "border-secondary/40 bg-secondary/10" : "border-accent/40 bg-accent/10"}`}>
          <p className={`text-sm font-semibold ${testResult.success ? "text-secondary" : "text-accent"}`}>
            {testResult.message || (testResult.success ? "Google Meet test succeeded" : "Google Meet test failed or returned fallback")}
          </p>
          <div className="mt-2 text-sm text-text space-y-1">
            {testResult.result?.meetLink && (
              <p><span className="font-medium">Meet Link:</span> <a className="text-primary hover:underline" href={testResult.result.meetLink} target="_blank" rel="noreferrer">{testResult.result.meetLink}</a></p>
            )}
            {testResult.result?.eventId && (
              <p><span className="font-medium">Event ID:</span> {testResult.result.eventId}</p>
            )}
            {testResult.result?.calendarLink && (
              <p><span className="font-medium">Calendar Link:</span> <a className="text-primary hover:underline" href={testResult.result.calendarLink} target="_blank" rel="noreferrer">{testResult.result.calendarLink}</a></p>
            )}
            {testResult.diagnostics && (
              <div className="pt-2 text-xs text-text/80">
                <p className="font-medium">Diagnostics:</p>
                <p className="flex items-center gap-2">Client ID: {testResult.diagnostics.hasClientId ? <FaCheck className="text-secondary flex-shrink-0" /> : <FaTimes className="text-accent flex-shrink-0" />}</p>
                <p className="flex items-center gap-2">Client Secret: {testResult.diagnostics.hasClientSecret ? <FaCheck className="text-secondary flex-shrink-0" /> : <FaTimes className="text-accent flex-shrink-0" />}</p>
                <p className="flex items-center gap-2">Refresh Token: {testResult.diagnostics.hasRefreshToken ? <FaCheck className="text-secondary flex-shrink-0" /> : <FaTimes className="text-accent flex-shrink-0" />}</p>
                {testResult.diagnostics.details && (
                  <div className="mt-2">
                    <p className="font-medium">Details:</p>
                    <pre className="bg-background p-2 rounded text-xs whitespace-pre-wrap overflow-x-auto">{typeof testResult.diagnostics.details === 'string' ? testResult.diagnostics.details : JSON.stringify(testResult.diagnostics.details, null, 2)}</pre>
                  </div>
                )}
              </div>
            )}
            {testResult.error && (
              <p className="text-accent text-xs pt-2">Error: {testResult.error}</p>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
